import React, { useState, useEffect, useMemo } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, Typography, Box, Snackbar, Alert, AlertColor,
    IconButton, Drawer, Chip,
    TextField,
    MenuItem,
    InputLabel,
    Select,
    FormControl,
    SortDirection,
} from '@mui/material';
import {
    FaPlus, FaEdit, FaTrash, FaGift,
    FaSearch
} from 'react-icons/fa';
import { useFirebase } from '../Auth/FirebaseProvider';
import Loader from '../Common/Loader';
import { menuService } from '../../services/menuService';
import BundleEditPanel from './BundleEditPanel';
import { categoriesService } from '../../services/categoriesService';
import { bundlesService } from '../../services/bundleService';
import { getDisplayPrice, getItemPrice } from '../Common/Price';
import { Bundle, MenuItem as MenuItemType, FoodCategory } from '../../models/interfaces';
import { SortableTableCell } from '../Common/SortableTableCell';
import EmptyStateView from '../Common/EmptyView';

interface BundleInfo {
    totalItems: number;
    priceRange: {
        min: number;
        max: number;
    };
}
type SortField = 'name' | 'description' | 'available';

interface SnackbarState {
    open: boolean;
    message: string;
    severity: AlertColor;
}

const BundleEditor: React.FC = () => {
    const { foodTruckId } = useFirebase();
    const [orderBy, setOrderBy] = useState<SortField>('name');
    const [order, setOrder] = useState<SortDirection>('asc');
    const [searchTerm, setSearchTerm] = useState('');
    const [availabilityFilter, setAvailabilityFilter] = useState('all');

    const [bundles, setBundles] = useState<Bundle[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);
    const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);
    const [categories, setCategories] = useState<FoodCategory[]>([]);
    const [services, setServices] = useState<{
        bundleService: ReturnType<typeof bundlesService.getInstance> | null;
        menuService: ReturnType<typeof menuService.getInstance> | null;
        categoriesService: ReturnType<typeof categoriesService.getInstance> | null;
    }>({
        bundleService: null,
        menuService: null,
        categoriesService: null
    });

    const loadBundles = async (): Promise<void> => {
        if (!services.bundleService) return;

        try {
            const bundlesData = await services.bundleService.getBundles();
            setBundles(bundlesData);
        } catch (error) {
            console.error("Error reloading bundles:", error);
            setSnackbar({
                open: true,
                message: 'Erreur lors du rechargement des bundles',
                severity: 'error'
            });
        }
    };

    // Initialize services
    useEffect(() => {
        if (!foodTruckId) return;

        const initServices = () => {
            const bundleSrv = bundlesService.getInstance(foodTruckId);
            const menuSrv = menuService.getInstance(foodTruckId);
            const catSrv = categoriesService.getInstance(foodTruckId);

            setServices({
                bundleService: bundleSrv,
                menuService: menuSrv,
                categoriesService: catSrv
            });
        };

        initServices();
    }, [foodTruckId]);

    // Load data
    useEffect(() => {
        if (!services.bundleService || !services.menuService || !services.categoriesService) return;

        let isSubscribed = true;
        let menuUnsubscribe: (() => void) | undefined;

        const loadData = async () => {
            try {
                // Load categories
                const categoriesData = await services.categoriesService!.getCategories();
                if (isSubscribed) {
                    setCategories(categoriesData);
                }

                // Load bundles
                const bundlesData = await services.bundleService!.getBundles();
                if (isSubscribed) {
                    setBundles(bundlesData);
                }

                // Load menu
                const menuData = await services.menuService!.getMenu();
                if (isSubscribed) {
                    setMenuItems(menuData);
                }
                setLoading(false);

            } catch (error) {
                if (isSubscribed) {
                    console.error("Error loading data:", error);
                    setError("Erreur lors du chargement des données");
                    setLoading(false);
                }
            }
        };

        loadData();

        return () => {
            isSubscribed = false;
            if (menuUnsubscribe) {
                menuUnsubscribe();
            }
        };
    }, [services]);

    const calculateBundleInfo = (bundle: Bundle): BundleInfo => {
        const totalItems = bundle.options.reduce((sum, opt) => sum + opt.items.length, 0);

        const priceRange = bundle.options.reduce((acc, option) => {
            option.items.forEach(item => {
                const menuItem = menuItems.find(mi => mi.id === item.id);
                if (menuItem) {
                    const price = getItemPrice(menuItem) + (item.extraPrice || 0);
                    if (price < acc.min) acc.min = price;
                    if (price > acc.max) acc.max = price;
                }
            });
            return acc;
        }, { min: Infinity, max: -Infinity });

        return {
            totalItems,
            priceRange: priceRange.min !== Infinity ? priceRange : { min: 0, max: 0 }
        };
    };
    
    const handleSaveBundle = async (bundleData: Bundle): Promise<void> => {
        if (!services.bundleService) return;

        try {
            await services.bundleService.updateBundle(bundleData);
            await loadBundles();

            setSnackbar({
                open: true,
                message: 'Formule sauvegardée avec succès',
                severity: 'success'
            });
            handleCloseSidePanel();
        } catch (error) {
            console.error('Error saving bundle:', error);
            setSnackbar({
                open: true,
                message: 'Erreur lors de la sauvegarde de la formule',
                severity: 'error'
            });
        }
    };

    const handleDeleteBundle = async (bundleId?: string): Promise<void> => {
        if (!services.bundleService || !bundleId) return;

        try {
            await services.bundleService.deleteBundle(bundleId);
            await loadBundles();
            setSnackbar({
                open: true,
                message: 'Formule supprimée avec succès',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error deleting bundle:', error);
            setSnackbar({
                open: true,
                message: 'Erreur lors de la suppression de la formule',
                severity: 'error'
            });
        }
    };


    const handleOpenSidePanel = (bundle: Bundle | null = null) => {
        setSelectedBundle(bundle ? { ...bundle } : null);
        setIsSidePanelOpen(true);
    };

    const handleCloseSidePanel = () => {
        setIsSidePanelOpen(false);
        setSelectedBundle(null);
    };

    const handleRequestSort = (property: SortField) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Mettre à jour la fonction de tri dans filteredAndSortedBundles
    const filteredAndSortedBundles = useMemo(() => {
        return [...bundles]
            .filter(bundle =>
                (availabilityFilter === 'all' ||
                    (availabilityFilter === 'available' && bundle.available) ||
                    (availabilityFilter === 'unavailable' && !bundle.available)) &&
                bundle.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .sort((a, b) => {
                // Gérer le tri de manière typée
                switch (orderBy) {
                    case 'name':
                        return order === 'asc'
                            ? a.name.localeCompare(b.name)
                            : b.name.localeCompare(a.name);
                    case 'description':
                        return order === 'asc'
                            ? (a.description || '').localeCompare(b.description || '')
                            : (b.description || '').localeCompare(a.description || '');
                    case 'available':
                        return order === 'asc'
                            ? (a.available === b.available ? 0 : a.available ? -1 : 1)
                            : (a.available === b.available ? 0 : a.available ? 1 : -1);
                    default:
                        return 0;
                }
            });
    }, [bundles, availabilityFilter, searchTerm, orderBy, order]);
    
    if (loading) return <Loader />;
    if (error) return <Typography color="error">{error}</Typography>;

    const hasFilteredResults = filteredAndSortedBundles.length > 0;
    const hasAllBundles = bundles.length > 0;

    return (
        <Box sx={{ p: 0 }}>
            {/* Header de la page */}
            <Box sx={{ 
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                p: 3,
                mb: 3
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography 
                            variant="h4" 
                            component="h1" 
                            sx={{ 
                                fontWeight: 600,
                                color: 'text.primary',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                bgcolor: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <FaGift size={20} color="white" />
                            </Box>
                            Gestion des formules
                        </Typography>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: 'text.secondary',
                                mt: 0.5
                            }}
                        >
                            Gérez vos formules et menus combinés
                        </Typography>
                    </Box>
                    
                    {hasAllBundles && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<FaPlus />}
                            onClick={() => handleOpenSidePanel()}
                            sx={{ borderRadius: 2 }}
                        >
                            Ajouter une formule
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Contenu principal */}
            <Box sx={{ px: 3 }}>

            {hasAllBundles ? (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <FormControl sx={{ m: 1, minWidth: 120 }}>
                            <InputLabel id="availability-filter-label">Disponibilité</InputLabel>
                            <Select
                                labelId="availability-filter-label"
                                value={availabilityFilter}
                                onChange={(e) => setAvailabilityFilter(e.target.value)}
                                label="Disponibilité"
                            >
                                <MenuItem value="all">Tous</MenuItem>
                                <MenuItem value="available">Disponible</MenuItem>
                                <MenuItem value="unavailable">Indisponible</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <FaSearch style={{ marginRight: '0.5rem' }} />,
                            }}
                        />
                    </Box>

                    {hasFilteredResults ? (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <SortableTableCell
                                            field="name"
                                            label="Nom"
                                            currentSort={orderBy}
                                            currentOrder={order}
                                            onSort={handleRequestSort}
                                        />
                                        <SortableTableCell
                                            field="description"
                                            label="Description"
                                            currentSort={orderBy}
                                            currentOrder={order}
                                            onSort={handleRequestSort}
                                        />
                                        <SortableTableCell
                                            field="available"
                                            label="Disponibilité"
                                            currentSort={orderBy}
                                            currentOrder={order}
                                            onSort={handleRequestSort}
                                        />
                                        <TableCell>Composition</TableCell>
                                        <TableCell>Tarification</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredAndSortedBundles.map((bundle) => {
                                        const info = calculateBundleInfo(bundle);
                                        return (
                                            <TableRow key={bundle.id}>
                                                <TableCell>{bundle.name}</TableCell>
                                                <TableCell>{bundle.description}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={bundle.available ? 'Disponible' : 'Indisponible'}
                                                        color={bundle.available ? 'success' : 'error'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {bundle.options.length} options • {info.totalItems} articles
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {bundle.options.map(opt => opt.type).join(', ')}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{getDisplayPrice(bundle)}</TableCell>
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                        <IconButton
                                                            onClick={() => handleOpenSidePanel(bundle)}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: 'primary.main',
                                                                color: 'white',
                                                                '&:hover': { 
                                                                    bgcolor: 'primary.dark',
                                                                    transform: 'scale(1.05)'
                                                                }
                                                            }}
                                                        >
                                                            <FaEdit size={14} />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={() => handleDeleteBundle(bundle.id)}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: 'error.main',
                                                                color: 'white',
                                                                '&:hover': { 
                                                                    bgcolor: 'error.dark',
                                                                    transform: 'scale(1.05)'
                                                                }
                                                            }}
                                                        >
                                                            <FaTrash size={14} />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Paper sx={{ p: 4, mt: 2, textAlign: 'center', backgroundColor: 'background.default' }}>
                            <Typography variant="h6" color="text.secondary">
                                Aucun résultat ne correspond à vos critères de recherche
                            </Typography>
                            <Button
                                variant="text"
                                color="primary"
                                onClick={() => {
                                    setSearchTerm('');
                                    setAvailabilityFilter('all');
                                }}
                                sx={{ mt: 2 }}
                            >
                                Réinitialiser les filtres
                            </Button>
                        </Paper>
                    )}
                </>
            ) : (
                <EmptyStateView 
                    icon={<FaGift size={60} />}
                    title="Aucune formule disponible"
                    description="Vous n'avez pas encore créé de formules pour votre menu. Les formules permettent de regrouper plusieurs articles et d'offrir un prix avantageux à vos clients."
                    buttonText="Créer votre première formule"
                    onAction={() => handleOpenSidePanel()}
                />
            )}
            </Box>

            <Drawer
                anchor="right"
                open={isSidePanelOpen}
                onClose={handleCloseSidePanel}
            >
                <Box sx={{ width: 800 }}>
                    <BundleEditPanel
                        bundle={selectedBundle}
                        onSave={handleSaveBundle}
                        onClose={handleCloseSidePanel}
                        menuItems={menuItems}
                        categories={categories}
                    />
                </Box>
            </Drawer>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default BundleEditor;